// src/components/bonos/SeccionBonosPaciente.js
// =========================================================================
// 🎟️ BONOS DEL PACIENTE
// Saldo de cada bono (sesiones usadas / restantes, caducidad) y, para
// recepción y dirección, venta de bonos del tarifario. Vender un bono emite
// su factura en el momento; las sesiones se descuentan al dar cita.
// =========================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useClinica } from '../../context/ClinicaContext';
import { useToast } from '../../context/ToastContext';
import { euros } from '../../utils/formatos';

const ESTILO_ESTADO = {
    activo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    agotado: 'bg-slate-100 text-slate-500 border-slate-200',
    caducado: 'bg-amber-50 text-amber-700 border-amber-200',
    anulado: 'bg-rose-50 text-rose-700 border-rose-200'
};
const TEXTO_ESTADO = { activo: 'Activo', agotado: 'Agotado', caducado: 'Caducado', anulado: 'Anulado' };

function ModalVenderBono({ pacienteId, plantillas, onCerrar, onVendido }) {
    const { showToast } = useToast();
    const [plantilla, setPlantilla] = useState(plantillas[0]?.codigo || '');
    const [metodoPago, setMetodoPago] = useState('tarjeta');
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const elegida = plantillas.find(p => p.codigo === plantilla);

    const vender = async () => {
        setGuardando(true);
        setError('');
        try {
            const res = await api.post('/bonos', { paciente: pacienteId, plantilla, metodoPago });
            showToast(`Bono vendido. Factura ${res.factura?.numeroFactura || ''} emitida.`, 'success');
            onVendido();
        } catch (err) {
            setError(err.message);
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" role="presentation"
             onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
            <div role="dialog" aria-modal="true" aria-label="Vender bono" className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 space-y-5">
                <h3 className="text-xl font-black text-slate-900">Vender bono</h3>

                <div className="space-y-2" role="radiogroup" aria-label="Tipo de bono">
                    {plantillas.map(p => (
                        <button key={p.codigo} type="button" role="radio" aria-checked={plantilla === p.codigo} onClick={() => setPlantilla(p.codigo)}
                                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${plantilla === p.codigo ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
                            <span className="flex justify-between gap-2">
                                <span className="text-sm font-black text-slate-900">{p.nombre}</span>
                                <span className="text-sm font-black text-slate-900">{euros(Number(p.precio))}</span>
                            </span>
                            <span className="block text-xs font-semibold text-slate-500 mt-0.5">
                                {p.sesiones} sesiones · {euros((p.precio / p.sesiones))}/sesión · {p.validezDias ? `válido ${p.validezDias} días` : 'sin caducidad'}
                            </span>
                        </button>
                    ))}
                </div>

                <div>
                    <div className="mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Método de pago</div>
                    <div className="flex gap-2">
                        {[['tarjeta', 'Tarjeta'], ['efectivo', 'Efectivo'], ['transferencia', 'Transf.']].map(([v, t]) => (
                            <button key={v} type="button" aria-pressed={metodoPago === v} onClick={() => setMetodoPago(v)}
                                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-bold ${metodoPago === v ? 'border-slate-900 bg-slate-50 text-slate-900' : 'border-slate-200 text-slate-500'}`}>
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {error && <p className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700" role="alert">{error}</p>}

                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onCerrar} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100">Cancelar</button>
                    <button type="button" onClick={vender} disabled={guardando || !elegida}
                            className="px-5 py-2.5 rounded-xl font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                        {guardando ? 'Cobrando…' : `Cobrar ${elegida ? euros(elegida.precio) : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SeccionBonosPaciente({ pacienteId }) {
    const { user } = useAuth();
    const { bonos: plantillas } = useClinica();
    const { showToast } = useToast();
    const rol = user?.role || user?.rol;
    const puedeVender = ['recepcion', 'supermedico', 'admin'].includes(rol);
    const puedeAnular = ['supermedico', 'admin'].includes(rol);
    const plantillasActivas = plantillas.filter(p => p.activo);

    const [bonos, setBonos] = useState(null);
    const [error, setError] = useState('');
    const [vendiendo, setVendiendo] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const res = await api.get(`/bonos?paciente=${pacienteId}`);
            setBonos(Array.isArray(res) ? res : []);
            setError('');
        } catch (err) {
            setError(err.message);
            setBonos([]);
        }
    }, [pacienteId]);

    useEffect(() => { cargar(); }, [cargar]);

    const anular = async (bono) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Anular «${bono.nombre}»? La devolución del dinero se hace con una factura rectificativa.`)) return;
        try {
            await api.delete(`/bonos/${bono.id}`);
            showToast('Bono anulado.', 'success');
            cargar();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    return (
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                <div>
                    <h3 className="text-xl font-black text-slate-900">Bonos de sesiones</h3>
                    <p className="text-sm font-medium text-slate-500 mt-1">Al dar cita se puede descontar la sesión de un bono activo.</p>
                </div>
                {puedeVender && plantillasActivas.length > 0 && (
                    <button type="button" onClick={() => setVendiendo(true)}
                            className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 shadow-md">
                        + Vender bono
                    </button>
                )}
            </div>

            {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
            {bonos === null && <div className="h-24 bg-slate-50 rounded-2xl animate-pulse" />}
            {bonos?.length === 0 && !error && (
                <p className="text-sm font-semibold text-slate-400 text-center py-6">
                    Este paciente no tiene bonos.{puedeVender && plantillasActivas.length === 0 ? ' Configura los bonos en «Tarifario».' : ''}
                </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bonos?.map(b => (
                    <div key={b.id} className="p-5 rounded-2xl border border-slate-200 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-black text-slate-900">{b.nombre}</p>
                            <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-widest ${ESTILO_ESTADO[b.estado]}`}>
                                {TEXTO_ESTADO[b.estado]}
                            </span>
                        </div>
                        <div>
                            <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                                <span>{b.usadas} de {b.sesiones} usadas</span>
                                <span className="text-slate-900">Quedan {b.restantes}</span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={b.sesiones} aria-valuenow={b.usadas} aria-label="Sesiones usadas">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(b.usadas / b.sesiones) * 100}%` }} />
                            </div>
                        </div>
                        <p className="text-[11px] font-semibold text-slate-400">
                            Comprado el {format(new Date(b.fechaCompra), 'dd/MM/yyyy')} · {euros(Number(b.precio))}
                            {b.caducaEl ? ` · caduca el ${format(new Date(b.caducaEl), 'dd/MM/yyyy')}` : ' · sin caducidad'}
                        </p>
                        {puedeAnular && b.usadas === 0 && b.estado === 'activo' && (
                            <button type="button" onClick={() => anular(b)} className="text-xs font-black text-rose-600 hover:underline">Anular bono</button>
                        )}
                    </div>
                ))}
            </div>

            {vendiendo && (
                <ModalVenderBono
                    pacienteId={pacienteId}
                    plantillas={plantillasActivas}
                    onCerrar={() => setVendiendo(false)}
                    onVendido={() => { setVendiendo(false); cargar(); }}
                />
            )}
        </div>
    );
}
